import { useState, useEffect, useMemo } from "react";
import { Inspection, Industry, ChecklistNormReference } from "../../../api";
import { useAuth } from "../../../hooks";
import { NORM_COUNTRIES } from "./normCountries";
import { ChlCardIcon } from "./ChlCardIcon";
import "./ChecklistTemplateCatalog.scss";

const inspCtrl     = new Inspection();
const industryCtrl = new Industry();
const normRefCtrl  = new ChecklistNormReference();

function uniqueCategories(items = []) {
  return [...new Set(items.map((i) => i.category).filter(Boolean))].length;
}

// Grilla de checklists activos filtrada por industria del sitio activo (ver
// useChecklistSiteIndustry), con búsqueda por nombre/industria/descripción.
// Compartido por el paso 1 de "Nueva inspección" (selección + continuar) y
// el tab "Explorar checklists" (selección = arranca la inspección directo).
export function useTemplateCatalog(siteIndustry) {
  const { accessToken } = useAuth();

  const [templates,  setTemplates]  = useState([]);
  const [industries, setIndustries] = useState([]);
  const [normRefs,   setNormRefs]   = useState([]);
  const [search,     setSearch]     = useState("");
  const [filterInd,  setFilterInd]  = useState("");
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const industryId = siteIndustry?._id || siteIndustry || undefined;
        const [tpls, inds, norms] = await Promise.all([
          inspCtrl.getTemplates(accessToken, { active: true, ...(industryId ? { industry: industryId } : {}) }),
          industryCtrl.getIndustries(accessToken, true),
          normRefCtrl.getNormReferences(accessToken).catch(() => []),
        ]);
        setTemplates(Array.isArray(tpls) ? tpls : []);
        setIndustries(Array.isArray(inds) ? inds : []);
        setNormRefs(Array.isArray(norms) ? norms : []);
      } catch {
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [accessToken, siteIndustry]);

  const industryOptions = [
    { key: "all", value: "", text: "Todas las industrias" },
    ...industries.map((i) => ({ key: i._id, value: i._id, text: i.name })),
  ];

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (filterInd && t.industry?._id !== filterInd) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !t.name.toLowerCase().includes(q) &&
          !(t.industry?.name || "").toLowerCase().includes(q) &&
          !(t.description || "").toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [templates, search, filterInd]);

  return { templates: filtered, industryOptions, normRefs, search, setSearch, filterInd, setFilterInd, loading };
}

export function TemplateCard({ tpl, selected, onClick, country, normRefs }) {
  // Con `country` explícito (Explorar, que tiene su propio filtro "Norma")
  // solo se muestra la norma de ese país puntual. Sin ese prop (ej. el
  // wizard de ChecklistForm, que no tiene selector de país) se muestra la
  // primera norma activa que haya cargada para la plantilla, sea cual sea
  // el país — mejor eso que no mostrar ninguna.
  const norm = !normRefs ? null
    : country
      ? normRefs.find((n) => (n.template?._id || n.template) === tpl._id && n.country === country && n.active !== false)
      : country === undefined
        ? normRefs.find((n) => (n.template?._id || n.template) === tpl._id && n.active !== false)
        : null;
  const normCountryText = norm ? (NORM_COUNTRIES.find((c) => c.value === norm.country)?.text || norm.country) : null;

  return (
    <div
      className={`ctpl__card${selected ? " ctpl__card--selected" : ""}`}
      onClick={() => onClick(tpl)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(tpl)}
    >
      <div className="ctpl__card__main">
        {tpl.industry?.name && (
          <div className="ctpl__card__industry-row">
            <ChlCardIcon />
            <span className="ctpl__card__industry-name">{tpl.industry.name}</span>
          </div>
        )}

        <span className="ctpl__card__name">{tpl.name}</span>

        {tpl.description && (
          <p className="ctpl__card__desc">{tpl.description}</p>
        )}

        <div className="ctpl__card__meta">
          <span>{tpl.items?.length ?? 0} ítems</span>
          <span>{uniqueCategories(tpl.items)} categorías</span>
          <span className={`ctpl__badge ${tpl.isSeeded ? "ctpl__badge--seed" : "ctpl__badge--own"}`}>
            {tpl.isSeeded ? "Semilla" : "Propia"}
          </span>
        </div>
      </div>

      {norm && (
        <div className="ctpl__card__norm">
          <strong>{normCountryText}:</strong> {norm.norm}
        </div>
      )}

      {selected && (
        <div className="ctpl__card__check">
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
      )}
    </div>
  );
}
